import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Container, Overline, Reveal } from "@/components/primitives";
import { submitWalkthrough } from "@/lib/api";
import { useDocumentMeta } from "@/lib/meta";

const fieldBase =
  "w-full border border-input bg-transparent px-4 py-3 text-foreground placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none focus:ring-0 transition-colors";

const productOptions = [
  "CaseMinder — operations",
  "ComplianceMinder — compliance",
  "Electra HR — people",
  "Training — AML, policy and practice areas",
  "The whole autonomy stack",
  "Not sure yet",
];

const firmSizes = ["1–5 fee earners", "6–15 fee earners", "16–40 fee earners", "40+ fee earners"];

export default function BookWalkthrough() {
  const [form, setForm] = useState({
    name: "",
    firm: "",
    email: "",
    role: "",
    firm_size: "",
    product_interest: "",
    current_systems: "",
    message: "",
  });
  useDocumentMeta({
    title: "Book a walkthrough",
    description:
      "Book a blunt, practical walkthrough of the Beyond the Box autonomy stack against your own firm — not a generic demo.",
    path: "/book-a-walkthrough",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.firm || !form.email) {
      toast.error("Please add your name, firm and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitWalkthrough(form);
      setDone(true);
      toast.success("Request received. We'll be in touch.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="walkthrough-page">
      <section className="border-b border-border">
        <Container className="grid grid-cols-1 gap-14 py-20 md:py-28 lg:grid-cols-12">
          {/* Left column — framing */}
          <div className="lg:col-span-5">
            <Reveal>
              <Overline accent>Book a walkthrough</Overline>
              <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.0] tracking-tight text-foreground md:text-6xl">
                See the autonomy stack against your firm.
              </h1>
              <p className="mt-7 max-w-md text-lg leading-relaxed text-muted-foreground">
                No pitch theatre. A blunt, practical walkthrough of how CaseMinder and
                ComplianceMinder would sit across the tools you already use — and where owner
                control, live compliance and real visibility would come from.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-10 border-t border-border">
                {[
                  "Built around how your teams actually work",
                  "Keep your best-of-class tools — Outlook, SharePoint, Teams",
                  "Own your data and working model, no vendor black box",
                ].map((t, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 border-b border-border py-4 text-sm text-foreground/90"
                  >
                    <Check strokeWidth={1.8} className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          {/* Right column — form or success */}
          <div className="lg:col-span-6 lg:col-start-7">
            {done ? (
              <div
                data-testid="walkthrough-success"
                className="flex h-full flex-col justify-center border border-border p-10 md:p-14"
              >
                <div className="flex h-12 w-12 items-center justify-center border border-accent text-accent">
                  <Check strokeWidth={1.8} className="h-6 w-6" />
                </div>
                <h2 className="mt-6 font-display text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
                  Request received.
                </h2>
                <p className="mt-4 max-w-md leading-relaxed text-muted-foreground">
                  Thank you, {form.name.split(" ")[0] || "there"}. We have your request and will be
                  in touch to arrange a walkthrough built around your firm — not a generic demo.
                </p>
                <div className="mt-8">
                  <Link
                    to="/"
                    data-testid="walkthrough-back-home"
                    className="inline-flex items-center gap-3 border border-foreground/40 px-6 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-foreground transition-colors hover:border-accent hover:text-accent"
                  >
                    Back to home <span>→</span>
                  </Link>
                </div>
              </div>
            ) : (
              <form
                data-testid="walkthrough-form"
                onSubmit={handleSubmit}
                className="border border-border p-7 md:p-10"
              >
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <label className="overline mb-2 block text-muted-foreground">Your name *</label>
                    <input
                      data-testid="walkthrough-name"
                      className={fieldBase}
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Partner"
                      required
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="overline mb-2 block text-muted-foreground">Firm *</label>
                    <input
                      data-testid="walkthrough-firm"
                      className={fieldBase}
                      value={form.firm}
                      onChange={update("firm")}
                      placeholder="Firm name"
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline mb-2 block text-muted-foreground">Work email *</label>
                    <input
                      data-testid="walkthrough-email"
                      type="email"
                      className={fieldBase}
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@firm.law"
                      required
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="overline mb-2 block text-muted-foreground">Your role</label>
                    <input
                      data-testid="walkthrough-role"
                      className={fieldBase}
                      value={form.role}
                      onChange={update("role")}
                      placeholder="Managing Partner, COLP…"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <label className="overline mb-2 block text-muted-foreground">Firm size</label>
                    <select
                      data-testid="walkthrough-firm-size"
                      className={fieldBase}
                      value={form.firm_size}
                      onChange={update("firm_size")}
                    >
                      <option value="">Select…</option>
                      {firmSizes.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline mb-2 block text-muted-foreground">
                      What are you most interested in?
                    </label>
                    <select
                      data-testid="walkthrough-product"
                      className={fieldBase}
                      value={form.product_interest}
                      onChange={update("product_interest")}
                    >
                      <option value="">Select…</option>
                      {productOptions.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline mb-2 block text-muted-foreground">
                      Current systems (optional)
                    </label>
                    <input
                      data-testid="walkthrough-systems"
                      className={fieldBase}
                      value={form.current_systems}
                      onChange={update("current_systems")}
                      placeholder="e.g. current practice-management platform, spreadsheets, external compliance retainer"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline mb-2 block text-muted-foreground">
                      Anything specific you want to cover?
                    </label>
                    <textarea
                      data-testid="walkthrough-message"
                      className={`${fieldBase} min-h-[120px] resize-y`}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us where the current pain is."
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="walkthrough-submit"
                  disabled={submitting}
                  className="mt-8 inline-flex w-full items-center justify-center gap-3 bg-primary px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-primary-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {submitting ? "Sending…" : "Request walkthrough"}
                  {!submitting && <span>→</span>}
                </button>
                <p className="mt-4 text-xs text-muted-foreground">
                  We use your details only to arrange a walkthrough. No spam, no retainers pushed.
                </p>
              </form>
            )}
          </div>
        </Container>
      </section>
    </div>
  );
}
